/**
 * Pricing I18N Helper
 *
 * Formats pricing display strings with localized currency and period suffixes.
 * Replaces hardcoded English strings from PRICING constant.
 *
 * Usage:
 * const displayPrice = formatPriceDisplay(9.99, 'month', t);
 * // English: "$9.99/mo"
 * // Spanish: "$9.99/mes"
 */

import type { TFunction } from '@dramadetector/shared';

type BillingInterval = 'week' | 'month' | 'quarter' | 'semester' | 'year';

/**
 * Map billing intervals to i18n translation keys for period suffixes
 */
const INTERVAL_SUFFIX_MAP: Record<BillingInterval, string> = {
  week: 'pricing.per_week_short',      // English: "/wk", Spanish: "/sem"
  month: 'pricing.per_month_short',    // English: "/mo", Spanish: "/mes"
  quarter: 'pricing.per_month_short',  // Display as monthly rate (e.g., "$8.33/mo")
  semester: 'pricing.per_month_short', // Display as monthly rate (e.g., "$7.99/mo")
  year: 'pricing.per_month_short',     // Display as monthly rate (e.g., "$7.49/mo")
};

/**
 * Format price with localized period suffix
 *
 * @param price - Numeric price (e.g., 9.99)
 * @param interval - Billing interval (week, month, quarter, semester, year)
 * @param t - Translation function from useTranslation('subscription')
 * @returns Formatted price string (e.g., "$9.99/mo" in English, "$9.99/mes" in Spanish)
 *
 * @example
 * const { t } = useTranslation('subscription');
 * formatPriceDisplay(9.99, 'month', t); // "$9.99/mo" (English) or "$9.99/mes" (Spanish)
 */
export function formatPriceDisplay(
  price: number,
  interval: BillingInterval,
  t: TFunction<"subscription">
): string {
  // Format price with 2 decimal places
  const formattedPrice = `$${price.toFixed(2)}`;

  // Get localized period suffix
  const suffixKey = INTERVAL_SUFFIX_MAP[interval];
  const suffix = t(suffixKey as any);

  return `${formattedPrice}${suffix}`;
}

/**
 * Format display price for BillingIntervalSelector (alternative signature)
 *
 * @param t - Translation function from useTranslation('subscription')
 * @param price - Numeric price (e.g., 9.99)
 * @param interval - Billing interval (week, month, quarter, semester, year)
 * @returns Formatted price string (e.g., "$9.99/mo" in English, "$9.99/mes" in Spanish)
 *
 * @example
 * const { t } = useTranslation('subscription');
 * formatDisplayPrice(t, 9.99, 'month'); // "$9.99/mo" (English) or "$9.99/mes" (Spanish)
 */
export function formatDisplayPrice(
  t: TFunction<"subscription">,
  price: number,
  interval: BillingInterval
): string {
  return formatPriceDisplay(price, interval, t);
}

/**
 * Format "billed as" text with localized period
 *
 * @param t - Translation function from useTranslation('subscription')
 * @param totalPrice - Total amount charged (e.g., 24.99 for quarterly)
 * @param interval - Billing interval (week, month, quarter, semester, year)
 * @returns Formatted billing text (e.g., "Billed as $24.99 every 3 months")
 *
 * @example
 * const { t } = useTranslation('subscription');
 * formatBilledAs(t, 24.99, 'quarter'); // "Billed as $24.99 every 3 months"
 */
export function formatBilledAs(
  t: TFunction<"subscription">,
  totalPrice: number,
  interval: BillingInterval
): string {
  const formattedPrice = `$${totalPrice.toFixed(2)}`;

  // Map interval to billing period text
  const billingPeriodMap: Record<BillingInterval, string> = {
    week: t('pricing.billed_monthly' as any), // Weekly shows as monthly equivalent
    month: t('pricing.billed_monthly' as any),
    quarter: t('pricing.every_months' as any, { count: 3 } as any),
    semester: t('pricing.every_months' as any, { count: 6 } as any),
    year: t('pricing.every_year' as any),
  };

  return `${formattedPrice}${billingPeriodMap[interval]}`;
}

/**
 * Format badge text with localization
 *
 * @param t - Translation function from useTranslation('subscription')
 * @param badgeKey - Badge type ('new', 'best_value')
 * @returns Translated badge text (e.g., "⭐ Best Value" in English, "⭐ Mejor Valor" in Spanish)
 *
 * @example
 * const { t } = useTranslation('subscription');
 * formatBadge(t, 'best_value'); // "⭐ Best Value" (English) or "⭐ Mejor Valor" (Spanish)
 */
export function formatBadge(
  t: TFunction<"subscription">,
  badgeKey: 'new' | 'best_value'
): string {
  const badges: Record<string, string> = {
    new: t('badges.new' as any),
    best_value: `⭐ ${t('badges.best_value' as any)}`,
  };

  return badges[badgeKey] || '';
}
